import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { apiErr, runJob } from "@/lib/api";
import { ui } from "@/lib/ui";
import { Sparkles, Loader2, X, Plus, Wand2, Check } from "lucide-react";

function Select({ value, onChange, children, ...props }) {
  return (
    <div className="relative">
      <select value={value} onChange={onChange}
        className={`${ui.input} appearance-none pr-9 cursor-pointer`} {...props}>
        {children}
      </select>
      <svg className="w-4 h-4 text-slate-500 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" viewBox="0 0 20 20" fill="currentColor"><path d="M6 8l4 4 4-4" stroke="currentColor" strokeWidth="1.5" fill="none"/></svg>
    </div>
  );
}

export default function WebsiteCreation() {
  const navigate = useNavigate();
  const [profiles, setProfiles] = useState([]);
  const [profileId, setProfileId] = useState("");
  const [b, setB] = useState({ name: "", phone: "", address: "", city: "", state: "", zip: "", logo_url: "" });
  const [areas, setAreas] = useState([]);
  const [areaInput, setAreaInput] = useState("");
  const [selected, setSelected] = useState([]);
  const [suggesting, setSuggesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [logo, setLogo] = useState(null);
  const [logoBusy, setLogoBusy] = useState(false);

  const profile = profiles.find((p) => p.id === profileId);

  useEffect(() => {
    api.get("/profiles").then(({ data }) => {
      setProfiles(data);
      if (data[0]) setProfileId(data[0].id);
    }).catch(() => toast.error("Failed to load categories"));
  }, []);

  useEffect(() => {
    if (profile) setSelected(profile.services.map((s) => s.anchor));
  }, [profileId]); // eslint-disable-line

  const set = (k, v) => setB((p) => ({ ...p, [k]: v }));

  const addArea = (val) => {
    const v = (val ?? areaInput).trim();
    if (v && !areas.includes(v)) setAreas((a) => [...a, v]);
    setAreaInput("");
  };

  const suggestAreas = async () => {
    if (!b.city || !b.state) { toast.error("Enter City and State first"); return; }
    setSuggesting(true);
    try {
      const { data } = await api.post("/service-area-suggest", { city: b.city, state: b.state });
      const merged = Array.from(new Set([...areas, ...data.areas]));
      setAreas(merged);
      toast.success(`Suggested ${data.areas.length} areas — edit as needed`);
    } catch (e) { toast.error(apiErr(e)); } finally { setSuggesting(false); }
  };

  const toggleService = (anchor) =>
    setSelected((s) => (s.includes(anchor) ? s.filter((x) => x !== anchor) : [...s, anchor]));

  const genLogo = async () => {
    if (!b.name || !profile) return toast.error("Enter a business name and pick a category first");
    setLogoBusy(true);
    try {
      const data = await runJob("/logo/preview", { business_name: b.name, category: profile.category_name });
      setLogo(data);
      toast.success("Logo ready — it'll be applied to this site");
    } catch (e) { toast.error(apiErr(e)); } finally { setLogoBusy(false); }
  };

  const save = async () => {
    if (!profileId) return toast.error("Pick a category");
    if (!b.name || !b.phone || !b.address || !b.city || !b.state || !b.zip)
      return toast.error("Fill in all required business fields");
    setSaving(true);
    try {
      const { data } = await api.post("/sites", {
        category_profile_id: profileId,
        business: { ...b, hours_247: true, hours: "", logo_url: logo?.logo_url || b.logo_url || "" },
        service_area: areas,
        selected_services: selected,
        theme_override: logo?.palette || [],
      });
      toast.success("Draft saved");
      navigate(`/websites/${data.id}`);
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };

  return (
    <div className="animate-fade-up">
      <div className="inline-flex rounded-lg border border-slate-800 p-1 mb-5" data-testid="website-type-toggle">
        <span className="px-3 py-1.5 rounded-md text-sm bg-indigo-600 text-white font-semibold">Local Business Website</span>
        <button data-testid="type-national-btn" onClick={() => navigate("/create/national")} className="px-3 py-1.5 rounded-md text-sm text-slate-400 hover:text-slate-100">National Website</button>
      </div>
      <h1 className={ui.pageHeader}>Website Creation</h1>
      <p className={`${ui.muted} mt-1 mb-8`}>Fill in the business details and save a draft, then generate the site.</p>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className={`${ui.card} p-6`}>
            <h2 className={`${ui.sectionHeader} mb-4`}>Business</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <label className={ui.label}>Category</label>
                <Select data-testid="create-category" value={profileId} onChange={(e) => setProfileId(e.target.value)}>
                  {profiles.map((p) => <option key={p.id} value={p.id}>{p.category_name}</option>)}
                </Select>
              </div>
              <div className="sm:col-span-2">
                <label className={ui.label}>Business Name</label>
                <input data-testid="create-name" className={ui.input} value={b.name} onChange={(e) => set("name", e.target.value)} placeholder="Laurelhurst Chimney Sweep" />
              </div>
              <div>
                <label className={ui.label}>Phone</label>
                <input data-testid="create-phone" className={ui.input} value={b.phone} onChange={(e) => set("phone", e.target.value)} placeholder="(425) 963-6246" />
              </div>
              <div>
                <label className={ui.label}>ZIP</label>
                <input data-testid="create-zip" className={ui.input} value={b.zip} onChange={(e) => set("zip", e.target.value)} placeholder="98105" />
              </div>
              <div className="sm:col-span-2">
                <label className={ui.label}>Full Address</label>
                <input data-testid="create-address" className={ui.input} value={b.address} onChange={(e) => set("address", e.target.value)} placeholder="4546 University Wy NE" />
              </div>
              <div>
                <label className={ui.label}>City</label>
                <input data-testid="create-city" className={ui.input} value={b.city} onChange={(e) => set("city", e.target.value)} placeholder="Seattle" />
              </div>
              <div>
                <label className={ui.label}>State</label>
                <input data-testid="create-state" className={ui.input} value={b.state} onChange={(e) => set("state", e.target.value)} placeholder="WA" />
              </div>
              <div className="sm:col-span-2">
                <label className={ui.label}>Logo URL (optional)</label>
                <input data-testid="create-logo" className={ui.input} value={b.logo_url} onChange={(e) => set("logo_url", e.target.value)} placeholder="https://…" />
              </div>
            </div>
          </div>

          <div className={`${ui.card} p-6`}>
            <div className="flex items-center justify-between mb-4">
              <h2 className={ui.sectionHeader}>Service Area</h2>
              <button data-testid="ai-suggest-areas" onClick={suggestAreas} disabled={suggesting} className={ui.btnGhost}>
                {suggesting ? <Loader2 className="w-4 h-4 spin" /> : <Sparkles className="w-4 h-4 text-indigo-400" />} AI Suggest
              </button>
            </div>
            <div className="flex gap-2 mb-3">
              <input data-testid="area-input" className={ui.input} value={areaInput} onChange={(e) => setAreaInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addArea(); } }} placeholder="Add a neighborhood or city…" />
              <button data-testid="area-add" onClick={() => addArea()} className={ui.btnGhost}><Plus className="w-4 h-4" /></button>
            </div>
            <div className="flex flex-wrap gap-2" data-testid="area-chips">
              {areas.map((a) => (
                <span key={a} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-slate-800 border border-slate-700 text-sm text-slate-200">
                  {a}
                  <button onClick={() => setAreas((x) => x.filter((y) => y !== a))} className="text-slate-500 hover:text-rose-400"><X className="w-3.5 h-3.5" /></button>
                </span>
              ))}
              {areas.length === 0 && <span className="text-sm text-slate-600">No areas yet — add manually or use AI Suggest.</span>}
            </div>
          </div>

          <div className={`${ui.card} p-6`}>
            <h2 className={`${ui.sectionHeader} mb-4`}>Services</h2>
            <div className="grid sm:grid-cols-2 gap-2">
              {profile?.services.map((s) => (
                <label key={s.anchor} className="flex items-center gap-2.5 px-3 py-2 rounded-lg hover:bg-slate-800/60 cursor-pointer text-sm text-slate-200">
                  <input type="checkbox" data-testid={`service-${s.anchor}`} checked={selected.includes(s.anchor)} onChange={() => toggleService(s.anchor)} className="w-4 h-4 accent-indigo-500" />
                  {s.name}
                </label>
              ))}
            </div>
          </div>

          <div className={`${ui.card} p-6`} data-testid="logo-card">
            <div className="flex items-center justify-between mb-4">
              <h2 className={ui.sectionHeader}>Brand Logo</h2>
              <button type="button" data-testid="create-generate-logo" onClick={genLogo} disabled={logoBusy} className={ui.btnGhost}>{logoBusy ? <Loader2 className="w-4 h-4 spin" /> : <Wand2 className="w-4 h-4 text-indigo-400" />} {logo ? "Regenerate" : "Generate Logo"}</button>
            </div>
            {logo ? (
              <div>
                <div className="rounded-lg bg-white border border-slate-800 p-5 flex items-center justify-center"><img data-testid="create-logo-preview" src={logo.logo_url} alt="logo" style={{ maxHeight: 88, maxWidth: "100%" }} /></div>
                <div className="flex gap-2 mt-3 items-center"><span className="text-xs text-slate-500 font-mono uppercase">Colors</span>{logo.palette.map((c, i) => <span key={i} className="w-6 h-6 rounded border border-slate-700" style={{ background: c.hex }} />)}</div>
                <p className="text-xs text-emerald-400 mt-2">This logo &amp; its colors will be applied when you save the draft.</p>
              </div>
            ) : (
              <p className="text-sm text-slate-500">Auto-create a professional 2-color transparent logo from the business name — preview it here before saving. (~30–60s)</p>
            )}
          </div>
        </div>

        <div className="space-y-6">
          <div className={`${ui.card} p-6 sticky top-6`}>
            <h2 className={`${ui.sectionHeader} mb-2`}>Publish</h2>
            <p className="text-xs text-slate-500 mb-5">Every site is generated from your reference template with a text wordmark. You'll generate it on the next screen.</p>
            <button data-testid="save-draft" onClick={save} disabled={saving} className={`${ui.btnPrimary} w-full`}>
              {saving ? <Loader2 className="w-4 h-4 spin" /> : null} Save Draft
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
